/**
 * useChannels() - the channel master (Settings → Channels & customers) for components.
 *
 *   const { loaded, all, byCode, sales, internal, groups, bins } = useChannels();
 *   all       every channel (public fields: code, name, type, status, bin_key, uses, report, issue, color...)
 *   sales     channels that record sales and are not closed or onboarding (pickers). Never B2B customers or
 *             internal outlets: those stay out of overall revenue.
 *   filters   channels that can have sales to look at: as `sales`, plus closed ones (their history)
 *   targets   what a target can be set on, groups first (AMAZON); targetViews: what can be viewed, closed ones too
 *   internal  stock-issue outlets
 *   bins      [{ key, label }] physical stock bins (warehouse first)
 * Before the master loads, `loaded` is false and the lists fall back to config/platforms.js.
 *
 * <ChannelsProvider> (in App.js) loads GET /api/channels once a user is signed in; reloadChannels() refreshes it
 * (the Channels page calls it after a save).
 */
import React, { useEffect, useMemo, useSyncExternalStore } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PLATFORMS, getChannelsSnapshot, setChannels, subscribeChannels } from '../config/platforms';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/channels`;

export const reloadChannels = async () => {
  try {
    const { data } = await axios.get(API);
    setChannels(data);
    return data;
  } catch {
    return null;   // keep the fallback (or the last good list)
  }
};

const fallback = () => ({
  channels: Object.values(PLATFORMS).map(p => ({ code: p.id, name: p.name, type: p.id.startsWith('D2C') ? 'd2c' : 'marketplace',
    status: 'active', uses: ['sales', 'targets'], records_sales: true, color: p.color })),
  groups: [{ code: 'AMAZON', name: 'Amazon (all)', members: ['AMAZON_SELLER', 'AMAZON_RETAIL'] }],
  bins: [],
});

export const useChannels = () => {
  const snapshot = useSyncExternalStore(subscribeChannels, getChannelsSnapshot);
  return useMemo(() => {
    const data = snapshot || fallback();
    const all = data.channels || [];
    const open = c => c.status !== 'closed';
    return {
      loaded: Boolean(snapshot),
      all,
      byCode: Object.fromEntries(all.map(c => [c.code, c])),
      sales: all.filter(c => c.records_sales && open(c) && c.status !== 'onboarding'),
      filters: all.filter(c => c.records_sales && c.status !== 'onboarding'),
      targetViews: [
        ...(data.groups || []).map(g => ({ code: g.code, name: g.name })),
        ...all.filter(c => c.records_sales && c.status !== 'onboarding'),
      ],
      targets: [
        ...(data.groups || []).map(g => ({ code: g.code, name: g.name })),
        ...all.filter(c => c.records_sales && open(c) && c.status !== 'onboarding'
          && ((c.uses || []).includes('targets') || c.code === 'OTHERS')),
      ],
      internal: all.filter(c => c.type === 'internal' && open(c)),
      groups: data.groups || [],
      bins: data.bins || [],
    };
  }, [snapshot]);
};

export const ChannelsProvider = ({ children }) => {
  const { user } = useAuth();
  const email = user?.email;
  useEffect(() => {
    if (!email) return undefined;
    let timer;
    const load = async () => {
      if (!await reloadChannels()) timer = setTimeout(load, 30000);   // the fallback list stays until a load works
    };
    load();
    // Another admin may have set a channel Active: pick it up when this tab is used again (at most every 5 minutes)
    let last = Date.now();
    const onFocus = () => {
      if (Date.now() - last > 300000) { last = Date.now(); reloadChannels(); }
    };
    window.addEventListener('focus', onFocus);
    return () => { clearTimeout(timer); window.removeEventListener('focus', onFocus); };
  }, [email]);
  return <>{children}</>;
};

export default useChannels;
